import { BorderBox } from '@/components/atoms/BorderBox'
import { DynamicHeightScrollArea } from "@/components/atoms/DynamicHeightScrollArea"
import DecisionModal from "@/components/custom/InfoPopups/DecisionModal"
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup"
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { addCrmModalSimplified, addCrmModalSimplifiedWithConnectionWizard } from "@/features/crm/components/organisms/AddCrmModalSimplified"
import { importConfigsDialog } from '@/features/crm/components/organisms/ImportConfigsDialog/lib/dialog'
import { useDeleteCrm } from '@/features/crm/hooks/useMutation/useDeleteCrm'
import { useUpdateCrm } from "@/features/crm/hooks/useMutation/useUpdateCrm"
import { CRM_SYSTEMS_IDS } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen"
import { CrmConfigurationParameterInput, crmParametersFormSchema, CrmParametersFormSchemaType } from '@/features/parameters/components/atoms/CrmConfigurationParameterInput'
import { ProjectConfigurationParameterInput, projectParametersFormSchema, ProjectParametersFormSchemaType } from '@/features/parameters/components/atoms/ProjectConfigurationParameterInput'
import { SurveyConfigurationParameterInput, surveyParametersFormSchema, SurveyParametersFormSchemaType } from '@/features/parameters/components/atoms/SurveyConfigurationParameterInput'
import { parameterFormSchema, ParameterFormSchemaType } from "@/features/parameters/components/organisms/ParameterForm"
import { ParameterForm } from "@/features/parameters/components/organisms/ParameterForm/ParameterForm"
import { cn } from "@/lib/utils"
import { matchConfigurationData, matchConfigurationDataWithDefault, matchDefaultConfigurationData } from "@/lib/utils/crm/matchConfigurationData"
import { Configuration, FullConfiguration } from "@/types/crm/configuration/configuration"
import { ParameterData } from "@/types/crm/configuration/parameter"
import { zodResolver } from "@hookform/resolvers/zod"
import { FileSymlinkIcon, Loader, RefreshCcw, Trash2 } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"
import { useParams, useSearchParams } from "react-router-dom"
import { GeneralSettingsSchemaType } from "."
import { ManageOrganizationSecondaryTabs, useManageOrganizationContext } from "../ManageOrganizationContext"
import { generalSettingsSchema } from "./ManageOrganization.schema"
import { useUpdateCrmProject } from '@/features/crm_project/hooks/useMutation/useUpdateCrmProject'
import { useUpdateCrmConfig } from '@/features/crm_config/hooks/useMutation/useUpdateCrmConfig'
import { CrmComponentResolver } from '@/features/crm_system/components/atoms/CrmComponentResolver'
import { confirmDialog } from '@/components/confirm-dialog'
import { connectionsWizardDialogForCrmById } from '@/features/connection-wizard/components/organisms/connection-wizard-dialog/connections-wizard-dialog'

export function CrmTab() {

    return (
        <div className="flex items-stretch flex-1 gap-1">
            <CrmsList />
            <CrmSettings />
        </div>
    )
}

function CrmsList() {
    const { crmsData, setCrm, crm: selectedCrm } = useManageOrganizationContext()
    const { mutateAsync: deleteCrmAsync } = useDeleteCrm()

    if (!crmsData) return null
    const { data: crms, isLoading: isLoadingCrms, error: crmsError } = crmsData;
    const isRowSelected = (crmConfigId?: string | number, selectedCrmConfigId?: string | number) => String(crmConfigId) === String(selectedCrmConfigId);

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col flex-1 gap-2">
                <Label>CRM configurations</Label>
                <BorderBox variant='info' size='sm'>
                    The CRM configuration will be used for exporting, and all related parameters will be included in the export file. You can add a configuration for each project, survey, and additional identifiers for questions.
                </BorderBox>
                <BorderBox className='flex-col items-stretch flex-1 w-full bg-white'>
                    {(() => {
                        if (isLoadingCrms) return (
                            <div className="grid h-full place-items-center">
                                <Loader className="animate-spin" />
                            </div>
                        )
                        if (crmsError) return (
                            <div className="grid h-full place-items-center">
                                <p>Error loading CRMs</p>
                            </div>
                        )
                        if (crms?.length === 0) return (
                            <div className="flex flex-col items-center justify-center flex-1 w-full gap-2">
                                <div className="flex flex-col items-center gap-2">
                                    <p>No CRMs found</p>
                                    {/* <AddCrmProjectModal /> */}
                                    <Button
                                        onClick={async () => {
                                            await addCrmModalSimplifiedWithConnectionWizard()
                                        }}
                                        size='sm'
                                    >
                                        Add new CRM connection
                                    </Button>

                                </div>
                            </div>
                        )
                        return (
                            <DynamicHeightScrollArea
                                containerProps={{
                                    className: "flex-1 h-full",
                                }}
                                offset={72}
                            >
                                <div className="flex flex-col gap-2">
                                    {crms?.map((crm) => (
                                        <div key={crm?.id} className={cn("cursor-pointer flex items-center justify-between p-2 bg-zinc-50 border rounded-md", isRowSelected(crm?.id, selectedCrm?.id) && "bg-zinc-100 border-zinc-400")}>
                                            <button className="flex flex-col items-start w-full cursor-pointer" onClick={() => setCrm(crm)}>
                                                <p>{crm?.name}</p>
                                                <p className="text-xs capitalize">{crm?.crm_system}</p>
                                            </button>
                                            <TooltipProvider>
                                                <div className="flex gap-2">
                                                    <div className="flex justify-center gap-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                size='action'
                                                                className={`text-nowrap flex text-sm`}
                                                                onClick={() => {
                                                                    connectionsWizardDialogForCrmById({crmId: crm?.id})
                                                                }}
                                                            >
                                                                <FileSymlinkIcon className='size-4' />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            Open Connections Wizard
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                                <div className="flex justify-center gap-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="destructive"
                                                                size='action'
                                                                className={`text-nowrap flex text-sm`}
                                                                onClick={() => {
                                                                    DecisionModal({
                                                                        message: "Are you sure you want to delete this CRM?",
                                                                        title: "Delete CRM",
                                                                    }).then((decision) => {
                                                                        if (decision.isConfirmed) {
                                                                            deleteCrmAsync(crm.id)
                                                                                .then(() => {
                                                                                    SuccessPopup("CRM deleted successfully")
                                                                                }).catch((error) => {
                                                                                    console.error(error)
                                                                                    ErrorPopup("Error deleting CRM")
                                                                                })
                                                                        }
                                                                    })
                                                                }}
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            Delete
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                                </div>
                                            </TooltipProvider>
                                        </div>
                                    ))}
                                </div>
                            </DynamicHeightScrollArea>
                        )
                    })()}
                    {!!crms?.length && (
                        <div className="flex justify-end">
                            <Button
                                onClick={async () => {
                                    await addCrmModalSimplifiedWithConnectionWizard()
                                }}
                                size='sm'
                                variant='default'
                            >
                                Add new CRM connection
                            </Button>
                        </div>
                    )}
                </BorderBox>
            </div>
        </div>
    )
}

function CrmSettings() {
    const { crm, secondaryTab, setSecondaryTab } = useManageOrganizationContext()

    /* 
    * Handle Scroll Right
    */
    return (
        <div className="flex flex-col flex-1 gap-2">
            <Label>CRM Configuration Settings</Label>
            <BorderBox className="flex flex-1 w-full pl-3 bg-white">
                {crm ? (
                    <Tabs
                        value={secondaryTab}
                        onValueChange={(value) => { setSecondaryTab(value as ManageOrganizationSecondaryTabs) }}
                        className="flex flex-col flex-1 w-full"
                    >
                        <TabsList className="justify-between w-full [&>*]:flex-1 h-auto">
                            <TabsTrigger value="settings" className="">
                                <p>Settings</p>
                            </TabsTrigger>
                            <CrmComponentResolver
                                selectedCrmSystem={crm.crm_system}
                                crmSystemComponentsMap={{
                                    'airtable': (
                                        <TabsTrigger value="parameter_editor" disabled={!crm}>
                                            <p>CRM Parameters</p>
                                        </TabsTrigger>
                                    )
                                }}
                                fallback={(
                                    <>
                                        <TabsTrigger value="parameters" disabled={!crm}>
                                            <p>CRM Parameters</p>
                                        </TabsTrigger>
                                        <TabsTrigger value="parameter_editor" disabled={!crm}>
                                            <p>Parameter Editor</p>
                                        </TabsTrigger>
                                    </>
                                )}
                            />

                        </TabsList>
                        <TabsContent value="settings" className={cn("flex flex-col", {
                            "flex-1": secondaryTab === "settings",
                            "hidden": secondaryTab !== "settings"
                        })}>
                            <CrmSettingsTab />
                        </TabsContent>
                        <TabsContent value="parameters" className={cn("flex flex-col", {
                            "flex-1": secondaryTab === "parameters",
                            "hidden": secondaryTab !== "parameters",
                        })}>
                            <CrmParametersTab />
                        </TabsContent>
                        <TabsContent value="parameter_editor" className={cn("flex flex-col", {
                            "flex-1": secondaryTab === "parameter_editor",
                            "hidden": secondaryTab !== "parameter_editor",
                        })}>
                            <ParametersEditorTab />
                        </TabsContent>
                    </Tabs>
                ) : (
                    <div className="flex flex-col w-full h-full">
                        <div className="flex items-center justify-center grow">
                            <p className="text-sm italic">Pick CRM on the left side to continue</p>
                        </div>
                    </div>
                )}
            </BorderBox>
        </div>
    )
}

function CrmSettingsTab() {
    const { crm } = useManageOrganizationContext()
    const { orgId } = useParams()
    /* 
    * CRM
    */
    const { mutateAsync: updateCrmAsync } = useUpdateCrm(Number(orgId));
    /*
    * General Settings Form
    */
    const [selectCrmSystemKey, setSelectCrmSystemKey] = useState(Date.now())

    const generalSettingsForm = useForm<GeneralSettingsSchemaType>({
        resolver: zodResolver(generalSettingsSchema)
    })
    /* 
    * Default Values / Form Submit
    */

    useEffect(() => {
        if (!crm) return
        generalSettingsForm.reset({
            name: crm?.name || '',
            crm_system: crm?.crm_system || '',
            description: crm?.description || '',
        })
    }, [crm, generalSettingsForm])

    const onSubmitGeneralSettings = (data: GeneralSettingsSchemaType) => {
        updateCrmAsync({
            id: crm!.id,
            name: data.name,
            description: data.description,
            crm_system: data.crm_system ?? DEFAULT_CRM_SYSTEM,
        }).then(() => {
            SuccessPopup("CRM updated successfully")
        }).catch((error) => {
            console.error(error)
            ErrorPopup("Error updating CRM")
        })
    }
    return (
        <div className="flex flex-col flex-1 h-full">
            <DynamicHeightScrollArea
                containerProps={{
                    className: "flex-1 h-full",
                }}
                offset={60}
            >
                <Accordion type='multiple' defaultValue={['general', 'parameters']}>
                    <Form {...generalSettingsForm}>
                        <form
                            className="flex flex-col gap-2 px-1"
                        >
                            <AccordionItem value='general'>
                                <AccordionTrigger>
                                    CRM - General Settings
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-2 px-1">
                                    <FormField
                                        control={generalSettingsForm.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    CRM Name
                                                </FormLabel>
                                                <FormControl>
                                                    <Input {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={generalSettingsForm.control}
                                        name="crm_system"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    CRM System
                                                </FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value ?? undefined} key={selectCrmSystemKey}>
                                                    <FormControl>
                                                        <div className="flex gap-2">
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="Select CRM System (optional)" className="capitalize" />
                                                            </SelectTrigger>
                                                            <Button
                                                                variant="outline"
                                                                size="action"
                                                                className="w-10 h-10"
                                                                onClick={() => {
                                                                    setSelectCrmSystemKey(Date.now())
                                                                    field.onChange(null)
                                                                }}
                                                            >
                                                                <RefreshCcw size={15} />
                                                            </Button>
                                                        </div>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {CRM_SYSTEMS_IDS.map((system) => (
                                                            <SelectItem key={system} value={system} className="capitalize">
                                                                {system}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={generalSettingsForm.control}
                                        name="description"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    Description
                                                </FormLabel>
                                                <FormControl>
                                                    <Input {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />


                                </AccordionContent>
                            </AccordionItem>
                        </form>
                    </Form>
                </Accordion>
            </DynamicHeightScrollArea>
            <div className="flex justify-end gap-2 pt-2">
                <ImportCrmConfigsButton />
                <Button
                    size='sm'
                    onClick={() => {
                        generalSettingsForm.handleSubmit(onSubmitGeneralSettings)()
                    }}
                >
                    Save Changes
                </Button>
            </div>
        </div>
    )
}

function CrmParametersTab() {
    const { crm } = useManageOrganizationContext()
    const { orgId } = useParams()
    /* 
    * CRM
    */
    const { mutateAsync: updateCrmAsync } = useUpdateCrm(Number(orgId));
    /*
    * General Settings Form
    */

    const parameterSettingsForm = useForm<ParameterFormSchemaType>({
        resolver: zodResolver(parameterFormSchema)
    })
    /* 
    * Default Values / Form Submit
    */

    useEffect(() => {
        if (!crm) return
        const parameter: FullConfiguration['parameter'] = {
            crm: matchConfigurationData(crm, crm.configuration as Configuration)?.parameter?.crm || [],
            survey: matchConfigurationData(crm, crm.configuration as Configuration)?.parameter?.survey || [],
            project: matchConfigurationData(crm, crm.configuration as Configuration)?.parameter?.project || [],
            question: matchConfigurationData(crm, crm.configuration as Configuration)?.parameter?.question || [],
        }
        parameterSettingsForm.reset({
            parameter
        })
    }, [crm, parameterSettingsForm])

    const onSubmitGeneralSettings = (data: ParameterFormSchemaType) => {
        updateCrmAsync({
            id: crm!.id,
            configuration: {
                parameter: {
                    crm: data.parameter?.crm?.map((parameter) => {
                        return {
                            id: parameter.id,
                            name: parameter.name,
                        }
                    }) || [],
                    survey: data.parameter?.survey?.map((parameter) => {
                        return {
                            id: parameter.id,
                            name: parameter.name,
                        }
                    }) || [],
                    project: data.parameter?.project?.map((parameter) => {
                        return {
                            id: parameter.id,
                            name: parameter.name,
                        }
                    }) || [],
                    question: data.parameter?.question?.map((parameter) => {
                        return {
                            id: parameter.id,
                            name: parameter.name,
                        }
                    }) || [],
                },
            },
            configuration_data: {
                parameter: {
                    // TODO Make this into util function
                    crm: data.parameter?.crm?.reduce((result, item) => {
                        result[item.id] = item.options || [];
                        return result;
                    }, {} as Record<string, ParameterData[]>) || {},
                    survey: data.parameter?.survey?.reduce((result, item) => {
                        result[item.id] = item.options || [];
                        return result;
                    }, {} as Record<string, ParameterData[]>) || {},
                    project: data.parameter?.project?.reduce((result, item) => {
                        result[item.id] = item.options || [];
                        return result;
                    }, {} as Record<string, ParameterData[]>) || {},
                    question: data.parameter?.question?.reduce((result, item) => {
                        result[item.id] = item.options || [];
                        return result;
                    }, {} as Record<string, ParameterData[]>) || {},
                }
            }
        }).then(() => {
            SuccessPopup("CRM Parameters updated successfully")
        }).catch((error) => {
            console.error(error)
            ErrorPopup("Error updating CRM Parameters")
        })
    }

    const [searchParams] = useSearchParams();
    const parametersTab = searchParams.get("parametersTab");

    return (
        <div className="flex flex-col flex-1 h-full">
            <DynamicHeightScrollArea
                containerProps={{
                    className: "flex-1 h-full",
                }}
                offset={46}
            >
                <Accordion
                    type='multiple'
                    defaultValue={parametersTab ? [parametersTab] : []}
                >
                    <Form {...parameterSettingsForm}>
                        <form
                            className="flex flex-col gap-2 px-1"
                        >
                            <AccordionItem value='crm'>
                                <AccordionTrigger>
                                    CRM Parameters
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-2">
                                    <ParameterForm
                                        label="CRM"
                                        description="Are there data elements associated with this CRM that are needed for all projects?"
                                        parameterName={"crm"}
                                        form={parameterSettingsForm}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value='project'>
                                <AccordionTrigger>
                                    Project Parameters
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-2">
                                    <ParameterForm
                                        label="Project"
                                        description="Are there data elements associated with this CRM that are needed for all projects?"
                                        parameterName={"project"}
                                        form={parameterSettingsForm}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value='survey'>
                                <AccordionTrigger>
                                    Survey Parameters
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-2">
                                    <ParameterForm
                                        label="Survey"
                                        description="Are there data elements associated with this CRM that are needed for all projects?"
                                        parameterName={"survey"}
                                        form={parameterSettingsForm}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                            <AccordionItem value='question'>
                                <AccordionTrigger>
                                    Question Parameters
                                </AccordionTrigger>
                                <AccordionContent className="flex flex-col gap-2">
                                    <ParameterForm
                                        label="Question"
                                        description="Are there data elements associated with this CRM that are needed for all projects?"
                                        parameterName={"question"}
                                        form={parameterSettingsForm}
                                    />
                                </AccordionContent>
                            </AccordionItem>
                        </form>
                    </Form>
                </Accordion>
            </DynamicHeightScrollArea>
            <div className="flex justify-end gap-2 pt-2">
                <ImportCrmConfigsButton />
                <Button
                    size='sm'
                    onClick={() => {
                        parameterSettingsForm.handleSubmit(onSubmitGeneralSettings)()
                    }}
                >
                    Save Changes
                </Button>
            </div>
        </div>
    )
}

function ParametersEditorTab() {
    const { crm, crmProjectsData, crmConfigsData, setCrmProject, setCrmConfig, crmConfig, crmProject } = useManageOrganizationContext()

    const matchedConfigurationWithDefault = useMemo(() => {
        if (!crm) return null
        return matchConfigurationDataWithDefault(crm, crmProject, crmConfig)
    }, [crm, crmProject, crmConfig]);

    const crmMatchedConfigurationWithDefault = useMemo(
        () => matchedConfigurationWithDefault?.parameter?.crm, [matchedConfigurationWithDefault]
    );
    const projectMatchedConfigurationWithDefault = useMemo(
        () => matchedConfigurationWithDefault?.parameter?.project, [matchedConfigurationWithDefault]
    );
    const surveyMatchedConfigurationWithDefault = useMemo(
        () => matchedConfigurationWithDefault?.parameter?.survey, [matchedConfigurationWithDefault]
    );

    const crmForm = useForm<CrmParametersFormSchemaType>({
        resolver: zodResolver(crmParametersFormSchema)
    });
    const projectForm = useForm<ProjectParametersFormSchemaType>({
        resolver: zodResolver(projectParametersFormSchema)
    });

    const surveyForm = useForm<SurveyParametersFormSchemaType>({
        resolver: zodResolver(surveyParametersFormSchema)
    });
    const { mutateAsync: updateCrm, isPending: isPendingUpdateCrm } = useUpdateCrm()
    const { mutateAsync: updateCrmProject, isPending: isPendingUpdateCrmProject } = useUpdateCrmProject()
    const { mutateAsync: updateCrmConfig, isPending: isPendingUpdateCrmConfig } = useUpdateCrmConfig()

    useEffect(() => {
        if (crm?.selected_configuration) {
            crmForm.reset({
                selected_configuration: crm?.selected_configuration
            });
        }
    }, [crm, crmForm]);

    useEffect(() => {
        if (crmProject?.selected_configuration) {
            projectForm.reset({
                selected_configuration: crmProject.selected_configuration
            });
        }
    }, [crmProject, projectForm]);

    useEffect(() => {
        if (crmConfig?.selected_configuration) {
            surveyForm.reset({
                selected_configuration: crmConfig.selected_configuration
            });
        }
    }, [crmConfig, surveyForm]);

    const onSubmitCrm = (data: CrmParametersFormSchemaType) => {
        console.log("CRM parameters:", data);
        updateCrm({ id: crm!.id, ...data })
            .then(() => {
                SuccessPopup("CRM parameters updated successfully")
            })
            .catch((error) => {
                console.error(error)
                ErrorPopup("Error updating CRM parameters")
            })
    };
    const onSubmitProject = (data: ProjectParametersFormSchemaType) => {
        updateCrmProject({ id: crmProject!.id, ...data })
            .then(() => {
                SuccessPopup("Project parameters updated successfully")
            })
            .catch((error) => {
                console.error(error)
                ErrorPopup("Error updating Project parameters")
            })
    };

    const onSubmitSurvey = (data: SurveyParametersFormSchemaType) => {
        updateCrmConfig({ id: crmConfig!.id, ...data })
            .then(() => {
                SuccessPopup("Survey parameters updated successfully")
            })
            .catch((error) => {
                console.error(error)
                ErrorPopup("Error updating Survey parameters")
            })
    };

    return (
        <div className="flex flex-col flex-1 h-full">
            <DynamicHeightScrollArea
                containerProps={{
                    className: "flex-1 h-full",
                }}
                offset={46}
            >
                <div className="flex flex-col gap-2 mb-4">
                    <Form {...crmForm}>
                        <form onSubmit={crmForm.handleSubmit(onSubmitCrm)} className="space-y-4">
                            <div className="p-4 border rounded-md">
                                <h3 className="mb-3 font-semibold">CRM Parameters</h3>
                                {crmMatchedConfigurationWithDefault?.length === 0 && (
                                    <BorderBox variant="info" size="sm" className="mb-4">
                                        No CRM parameters defined. Please define CRM parameters in the "CRM Parameters" tab.
                                    </BorderBox>
                                )}
                                <CrmConfigurationParameterInput
                                    crmSystem={crm?.crm_system}
                                    fullConfiguration={matchedConfigurationWithDefault}
                                    parameters={crmMatchedConfigurationWithDefault}
                                    form={crmForm}
                                />
                                <div className="flex justify-end mt-4">
                                    <Button type="submit" size="sm" disabled={isPendingUpdateCrm}>
                                        {isPendingUpdateCrm ? (<Loader className="animate-spin" size={16} />) : 'Save CRM Parameters'}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form>
                    <Label className="mb-2">Project Configuration ({crmProjectsData?.data?.length ?? 0})</Label>
                    <Select value={crmProject ? String(crmProject.id) : undefined} onValueChange={(value) => {
                        const project = crmProjectsData?.data?.find(p => String(p.id) === value)
                        setCrmProject(project || null)
                    }}>
                        <SelectTrigger className="w-full mb-2">
                            <SelectValue placeholder="Select Project" />
                        </SelectTrigger>
                        <SelectContent>
                            {crmProjectsData?.data?.map((project) => (
                                <SelectItem
                                    key={project.id}
                                    value={String(project.id)}
                                >
                                    {project.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {crmProject && projectMatchedConfigurationWithDefault && projectMatchedConfigurationWithDefault.length > 0 && (
                        <Form {...projectForm}>
                            <form onSubmit={projectForm.handleSubmit(onSubmitProject)} className="space-y-4">
                                <div className="p-4 border rounded-md">
                                    <h3 className="mb-3 font-semibold">Project Parameters</h3>
                                    {projectMatchedConfigurationWithDefault?.length === 0 && (
                                        <BorderBox variant="info" size="sm" className="mb-4">
                                            No CRM Project parameters defined. Please define CRM Project parameters in the "CRM Parameters" tab.
                                        </BorderBox>
                                    )}
                                    <ProjectConfigurationParameterInput
                                        crmSystem={crm?.crm_system}
                                        fullConfiguration={matchedConfigurationWithDefault}
                                        parameters={projectMatchedConfigurationWithDefault}
                                        form={projectForm}
                                    />
                                    <div className="flex justify-end mt-4">
                                        <Button type="submit" size="sm" disabled={isPendingUpdateCrmProject}>
                                            {isPendingUpdateCrmProject ? (<Loader className="animate-spin" size={16} />) : 'Save Project Parameters'}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        </Form>
                    )}
                </div>
                {crmConfigsData?.isLoading && (
                    <div className="flex items-center justify-center gap-2">
                        <Loader className="animate-spin" size={16} />
                        <span>Loading Survey Configurations...</span>
                    </div>
                )}
                {crmProject && crmConfigsData?.isLoading === false && (
                    <div className="mb-4">
                        <Label className="mb-2">Survey Configuration ({crmConfigsData?.data?.length ?? 0})</Label>
                        <Select value={crmConfig ? String(crmConfig.id) : undefined} onValueChange={(value) => {
                            const config = crmConfigsData?.data?.find(c => String(c.id) === value)
                            setCrmConfig(config || null)
                        }}>
                            <SelectTrigger className="w-full mb-2">
                                <SelectValue placeholder="Select Survey" />
                            </SelectTrigger>
                            <SelectContent>
                                {crmConfigsData?.data?.map((config) => (
                                    <SelectItem
                                        key={config.id}
                                        value={String(config.id)}
                                    >
                                        {config.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {crmConfig && surveyMatchedConfigurationWithDefault && surveyMatchedConfigurationWithDefault.length > 0 && (
                            <Form {...surveyForm}>
                                <form onSubmit={surveyForm.handleSubmit(onSubmitSurvey)} className="space-y-4">
                                    <div className="p-4 border rounded-md">
                                        <h3 className="mb-3 font-semibold">Survey Parameters</h3>
                                        {surveyMatchedConfigurationWithDefault?.length === 0 && (
                                            <BorderBox variant="info" size="sm" className="mb-4">
                                                No CRM Survey parameters defined. Please define CRM Survey parameters in the "CRM Parameters" tab.
                                            </BorderBox>
                                        )}
                                        <SurveyConfigurationParameterInput
                                            crmSystem={crm?.crm_system}
                                            fullConfiguration={matchedConfigurationWithDefault}
                                            parameters={surveyMatchedConfigurationWithDefault}
                                            form={surveyForm}
                                        />
                                        <div className="flex justify-end mt-4">
                                            <Button type="submit" size="sm" disabled={isPendingUpdateCrmConfig}>
                                                {isPendingUpdateCrmConfig ? (<Loader className="animate-spin" size={16} />) : 'Save Survey Parameters'}
                                            </Button>
                                        </div>
                                    </div>
                                </form>
                            </Form>
                        )}
                    </div>
                )}
            </DynamicHeightScrollArea>
            <div className="flex justify-end gap-2 pt-2">
                <ImportCrmConfigsButton />
            </div>
        </div>
    )
}

function ImportCrmConfigsButton() {
    const { crm } = useManageOrganizationContext()
    const SHOW_BUTTON = crm !== null && crm !== undefined && crm.crm_system !== 'airtable' // Airtable does not support import yet
    if (!SHOW_BUTTON) return null
    return (
        <Button
            onClick={() => {
                importConfigsDialog({
                    crm: crm!,
                })
            }}
            size='sm'
            variant='accent'
        >
            Import CRM Configurations
        </Button>
    )
}